import axios from 'axios';
import fileDownload from 'js-file-download';
import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	TEuDdpCreateDeliveryOperatorRequest,
	TEuDdpCreatePricingPlanRequest,
	TEuDdpCreateTariffRequest,
	TEuDdpDeliveryOperatorMutationResponse,
	TEuDdpDeliveryOperatorSearchRequest,
	TEuDdpDeliveryOperatorsSearchResponse,
	TEuDdpPricingPlanMutationResponse,
	TEuDdpPricingPlanSearchRequest,
	TEuDdpPricingPlansSearchResponse,
	TEuDdpRecordSearchRequest,
	TEuDdpRecordsSearchResponse,
	TEuDdpTariffMutationResponse,
	TEuDdpTariffSearchRequest,
	TEuDdpTariffsSearchResponse,
	TEuDdpUpdateDeliveryOperatorRequest,
	TEuDdpUpdatePricingPlanRequest,
	TEuDdpUpdateTariffRequest,
	TEuDdpOrderSearchRequest,
	TEuDdpOrdersSearchResponse,
	TEuDdpOrderDetailsRequest,
	TEuDdpOrderDetailsResponse,
	TEuDdpLabelsResponse,
	TEuDdpOmnivaLabelRequest,
	TEuDdpInvoiceProformaRequest,
} from './types';
import {
	searchDeliveryOperatorsRequest,
	searchDeliveryOperatorsSuccess,
	searchDeliveryOperatorsFailure,
	createDeliveryOperatorRequest,
	createDeliveryOperatorSuccess,
	createDeliveryOperatorFailure,
	updateDeliveryOperatorRequest,
	updateDeliveryOperatorSuccess,
	updateDeliveryOperatorFailure,
	searchTariffsRequest,
	searchTariffsSuccess,
	searchTariffsFailure,
	createTariffRequest,
	createTariffSuccess,
	createTariffFailure,
	updateTariffRequest,
	updateTariffSuccess,
	updateTariffFailure,
	searchPricingPlansRequest,
	searchPricingPlansSuccess,
	searchPricingPlansFailure,
	createPricingPlanRequest,
	createPricingPlanSuccess,
	createPricingPlanFailure,
	updatePricingPlanRequest,
	updatePricingPlanSuccess,
	updatePricingPlanFailure,
	searchEuDdpRecordsRequest,
	searchEuDdpRecordsSuccess,
	searchEuDdpRecordsFailure,
	searchOrdersRequest,
	searchOrdersSuccess,
	searchOrdersFailure,
	getOrderDetailsRequest,
	getOrderDetailsSuccess,
	getOrderDetailsFailure,
	getPrintLabelsRequest,
	getPrintLabelsSuccess,
	getPrintLabelsFailure,
	getOmnivaLabelRequest,
	getOmnivaLabelSuccess,
	getOmnivaLabelFailure,
	getInvoiceProformaRequest,
	getInvoiceProformaSuccess,
	getInvoiceProformaFailure,
} from './reducer';

function* searchDeliveryOperatorsWorker(
	action: PayloadAction<TEuDdpDeliveryOperatorSearchRequest>,
) {
	try {
		const response: TEuDdpDeliveryOperatorsSearchResponse = yield call(
			api.euDdp.searchDeliveryOperators,
			action.payload,
		);
		yield put(searchDeliveryOperatorsSuccess(response));
	} catch (error) {
		yield put(searchDeliveryOperatorsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* createDeliveryOperatorWorker(action: PayloadAction<TEuDdpCreateDeliveryOperatorRequest>) {
	try {
		const response: TEuDdpDeliveryOperatorMutationResponse = yield call(
			api.euDdp.createDeliveryOperator,
			action.payload,
		);
		yield put(createDeliveryOperatorSuccess());
		notificationContainer(
			response?.message ?? 'Delivery operator created successfully.',
			'success',
		);
	} catch (error) {
		yield put(createDeliveryOperatorFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateDeliveryOperatorWorker(action: PayloadAction<TEuDdpUpdateDeliveryOperatorRequest>) {
	try {
		const response: TEuDdpDeliveryOperatorMutationResponse = yield call(
			api.euDdp.updateDeliveryOperator,
			action.payload,
		);
		yield put(updateDeliveryOperatorSuccess());
		notificationContainer(
			response?.message ?? 'Delivery operator updated successfully.',
			'success',
		);
	} catch (error) {
		yield put(updateDeliveryOperatorFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* searchTariffsWorker(action: PayloadAction<TEuDdpTariffSearchRequest>) {
	try {
		const response: TEuDdpTariffsSearchResponse = yield call(
			api.euDdp.searchTariffs,
			action.payload,
		);
		yield put(searchTariffsSuccess(response));
	} catch (error) {
		yield put(searchTariffsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* createTariffWorker(action: PayloadAction<TEuDdpCreateTariffRequest>) {
	try {
		const response: TEuDdpTariffMutationResponse = yield call(
			api.euDdp.createTariff,
			action.payload,
		);
		yield put(createTariffSuccess());
		notificationContainer(response?.message ?? 'Tariff created successfully.', 'success');
	} catch (error) {
		yield put(createTariffFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateTariffWorker(action: PayloadAction<TEuDdpUpdateTariffRequest>) {
	try {
		const response: TEuDdpTariffMutationResponse = yield call(
			api.euDdp.updateTariff,
			action.payload,
		);
		yield put(updateTariffSuccess());
		notificationContainer(response?.message ?? 'Tariff updated successfully.', 'success');
	} catch (error) {
		yield put(updateTariffFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* searchPricingPlansWorker(action: PayloadAction<TEuDdpPricingPlanSearchRequest>) {
	try {
		const response: TEuDdpPricingPlansSearchResponse = yield call(
			api.euDdp.searchPricingPlans,
			action.payload,
		);
		yield put(searchPricingPlansSuccess(response));
	} catch (error) {
		yield put(searchPricingPlansFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* createPricingPlanWorker(action: PayloadAction<TEuDdpCreatePricingPlanRequest>) {
	try {
		const response: TEuDdpPricingPlanMutationResponse = yield call(
			api.euDdp.createPricingPlan,
			action.payload,
		);
		yield put(createPricingPlanSuccess());
		notificationContainer(response?.message ?? 'Pricing plan created successfully.', 'success');
	} catch (error) {
		yield put(createPricingPlanFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updatePricingPlanWorker(action: PayloadAction<TEuDdpUpdatePricingPlanRequest>) {
	try {
		const response: TEuDdpPricingPlanMutationResponse = yield call(
			api.euDdp.updatePricingPlan,
			action.payload,
		);
		yield put(updatePricingPlanSuccess());
		notificationContainer(response?.message ?? 'Pricing plan updated successfully.', 'success');
	} catch (error) {
		yield put(updatePricingPlanFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* searchEuDdpRecordsWorker(action: PayloadAction<TEuDdpRecordSearchRequest>) {
	try {
		const response: TEuDdpRecordsSearchResponse = yield call(
			api.euDdp.searchEuDdpRecords,
			action.payload,
		);
		yield put(searchEuDdpRecordsSuccess(response));
	} catch (error) {
		yield put(searchEuDdpRecordsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* searchOrdersWorker(action: PayloadAction<TEuDdpOrderSearchRequest>) {
	try {
		const response: TEuDdpOrdersSearchResponse = yield call(api.euDdp.searchOrders, action.payload);
		yield put(searchOrdersSuccess(response));
	} catch (error) {
		yield put(searchOrdersFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOrderDetailsWorker(action: PayloadAction<TEuDdpOrderDetailsRequest>) {
	try {
		const response: TEuDdpOrderDetailsResponse = yield call(
			api.euDdp.getOrderDetails,
			action.payload,
		);
		yield put(getOrderDetailsSuccess(response));
	} catch (error) {
		yield put(getOrderDetailsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getPrintLabelsWorker() {
	try {
		const response: TEuDdpLabelsResponse = yield call(api.euDdp.getPrintLabels);
		yield put(getPrintLabelsSuccess(response));
	} catch (error) {
		yield put(getPrintLabelsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* getOmnivaLabelWorker(action: PayloadAction<TEuDdpOmnivaLabelRequest>) {
	try {
		const response: Blob = yield call(api.euDdp.getOmnivaLabel, action.payload.orderId);
		yield put(getOmnivaLabelSuccess());
		if (response) {
			fileDownload(response, action.payload.fileName);
		}
	} catch (error) {
		yield put(getOmnivaLabelFailure());
		if (axios.isAxiosError(error)) {
			const data = error?.response?.data;
			let message = 'Error';
			// For blob responses the error body is also a Blob, so we read it as text first.
			if (data instanceof Blob) {
				const text: string = yield call(() => data.text());
				try {
					const parsed = JSON.parse(text);
					message = String(parsed?.message ?? parsed ?? text ?? 'Error');
				} catch {
					message = text || 'Error';
				}
			} else {
				message = String(data?.message ?? data ?? error ?? 'Error');
			}
			notificationContainer(message, 'error');
		}
	}
}

function* getInvoiceProformaWorker(action: PayloadAction<TEuDdpInvoiceProformaRequest>) {
	try {
		const response: Blob = yield call(api.euDdp.getInvoiceProforma, action.payload.orderId);
		yield put(getInvoiceProformaSuccess());
		if (response) {
			fileDownload(response, action.payload.fileName);
		}
	} catch (error) {
		yield put(getInvoiceProformaFailure());
		if (axios.isAxiosError(error)) {
			const data = error?.response?.data;
			let message = 'Error';
			// For blob responses the error body is also a Blob, so we read it as text first.
			if (data instanceof Blob) {
				const text: string = yield call(() => data.text());
				try {
					const parsed = JSON.parse(text);
					message = String(parsed?.message ?? parsed ?? text ?? 'Error');
				} catch {
					message = text || 'Error';
				}
			} else {
				message = String(data?.message ?? data ?? error ?? 'Error');
			}
			notificationContainer(message, 'error');
		}
	}
}

export function* euDdpSaga() {
	yield takeLatest(searchDeliveryOperatorsRequest.type, searchDeliveryOperatorsWorker);
	yield takeLatest(createDeliveryOperatorRequest.type, createDeliveryOperatorWorker);
	yield takeLatest(updateDeliveryOperatorRequest.type, updateDeliveryOperatorWorker);
	yield takeLatest(searchTariffsRequest.type, searchTariffsWorker);
	yield takeLatest(createTariffRequest.type, createTariffWorker);
	yield takeLatest(updateTariffRequest.type, updateTariffWorker);
	yield takeLatest(searchPricingPlansRequest.type, searchPricingPlansWorker);
	yield takeLatest(createPricingPlanRequest.type, createPricingPlanWorker);
	yield takeLatest(updatePricingPlanRequest.type, updatePricingPlanWorker);
	yield takeLatest(searchEuDdpRecordsRequest.type, searchEuDdpRecordsWorker);
	yield takeLatest(searchOrdersRequest.type, searchOrdersWorker);
	yield takeLatest(getOrderDetailsRequest.type, getOrderDetailsWorker);
	yield takeLatest(getPrintLabelsRequest.type, getPrintLabelsWorker);
	yield takeLatest(getOmnivaLabelRequest.type, getOmnivaLabelWorker);
	yield takeLatest(getInvoiceProformaRequest.type, getInvoiceProformaWorker);
}
